import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { PLATFORMS } from '@/lib/platformConstants';

/**
 * Resets all POS training data for a company
 * This includes:
 * - POS menu item mappings
 * - POS report uploads (and their files from storage)
 * - POS training progress
 * - Sales transactions from POS reports (optional)
 * 
 * @param companyId - Company ID to reset data for
 * @param platform - Optional platform to reset (if not provided, resets all platforms)
 * @param includeSalesTransactions - Whether to also delete sales_transactions from POS reports (default: false)
 */
export async function resetPOSTrainingData(
  companyId: string,
  platform?: string,
  includeSalesTransactions: boolean = false
): Promise<void> {
  try {
    // 1. Delete POS menu item mappings
    let mappingsQuery = supabase
      .from('pos_menu_item_mappings')
      .delete()
      .eq('company_id', companyId);
    
    if (platform) {
      mappingsQuery = mappingsQuery.eq('platform', platform);
    }
    
    const { error: mappingsError } = await mappingsQuery;
    if (mappingsError) {
      console.error('Error deleting POS mappings:', mappingsError);
      throw new Error(`Failed to delete POS mappings: ${mappingsError.message}`);
    }

    // 2. Get report uploads before deleting (to delete files from storage)
    let reportsQuery = supabase
      .from('pos_report_uploads')
      .select('id, file_url, platform')
      .eq('company_id', companyId);
    
    if (platform) {
      reportsQuery = reportsQuery.eq('platform', platform);
    }
    
    const { data: reports, error: reportsFetchError } = await reportsQuery;
    
    if (reportsFetchError) {
      console.error('Error fetching reports for deletion:', reportsFetchError);
      // Continue anyway - we'll try to delete the records
    }

    // 3. Delete files from storage
    if (reports && reports.length > 0) {
      const fileUrls = reports
        .map(r => r.file_url)
        .filter((url): url is string => !!url);
      
      if (fileUrls.length > 0) {
        // Delete files in batches (Supabase storage has limits)
        const batchSize = 10;
        for (let i = 0; i < fileUrls.length; i += batchSize) {
          const batch = fileUrls.slice(i, i + batchSize);
          const { error: storageError } = await supabase.storage
            .from('company-documents')
            .remove(batch);
          
          if (storageError) {
            console.warn('Error deleting some files from storage:', storageError);
            // Continue - files might not exist or already deleted
          }
        }
      }
    }

    // 4. Delete POS report uploads
    let deleteReportsQuery = supabase
      .from('pos_report_uploads')
      .delete()
      .eq('company_id', companyId);
    
    if (platform) {
      deleteReportsQuery = deleteReportsQuery.eq('platform', platform);
    }
    
    const { error: deleteReportsError } = await deleteReportsQuery;
    if (deleteReportsError) {
      console.error('Error deleting POS report uploads:', deleteReportsError);
      throw new Error(`Failed to delete POS report uploads: ${deleteReportsError.message}`);
    }

    // 5. Delete POS training progress
    let progressQuery = supabase
      .from('pos_training_progress')
      .delete()
      .eq('company_id', companyId);
    
    if (platform) {
      progressQuery = progressQuery.eq('platform', platform);
    }
    
    const { error: progressError } = await progressQuery;
    if (progressError) {
      console.error('Error deleting POS training progress:', progressError);
      throw new Error(`Failed to delete POS training progress: ${progressError.message}`);
    }

    // 6. Optionally delete sales transactions from POS reports
    if (includeSalesTransactions) {
      // Delete sales transactions that came from POS reports
      // We can identify them by checking if they have a source that matches POS platforms
      const posPlatforms = platform ? [platform] : [PLATFORMS.TOAST, PLATFORMS.DOORDASH, PLATFORMS.UBER];
      
      for (const plat of posPlatforms) {
        const { error: salesError } = await supabase
          .from('sales_transactions')
          .delete()
          .eq('company_id', companyId)
          .or(`source.ilike.%${plat}%,source.ilike.%${plat.toUpperCase()}%`);
        
        if (salesError) {
          console.warn(`Error deleting sales transactions for ${plat}:`, salesError);
          // Continue - this is optional
        }
      }
    }

    console.log(`Successfully reset POS training data for company ${companyId}${platform ? ` (platform: ${platform})` : ' (all platforms)'}`);
  } catch (error: any) {
    console.error('Error resetting POS training data:', error);
    throw error;
  }
}

/**
 * Resets POS training data for the current user's company
 * Convenience wrapper around resetPOSTrainingData
 */
export async function resetCurrentUserPOSTrainingData(
  platform?: string,
  includeSalesTransactions: boolean = false
): Promise<void> {
  const companyId = await getCurrentUserCompanyId();
  if (!companyId) {
    throw new Error('Company ID not found');
  }
  
  return resetPOSTrainingData(companyId, platform, includeSalesTransactions);
}
